/**
 * PulseEventDetailModal — iter233
 * ────────────────────────────────────────────────────────────
 * When a user taps "View" on any Pulse card, this modal opens with
 * the full context: hero image / embedded video, headline, description,
 * and any custom links (label + url chips) that a founder attached to
 * the post. Portaled to <body> so backdrop-blur clipping can't cut it.
 */
import { createPortal } from 'react-dom';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  X, ExternalLink, ArrowRight, Activity, ShieldCheck, BadgeCheck, Sparkles,
  Newspaper, Film, Video, Pencil, Link as LinkIcon, Flag, Trophy, Award,
  Handshake, Radio, Rocket, Megaphone, Flame, UserPlus, ListChecks,
  ArrowRightLeft, Users, User as UserIcon,
} from 'lucide-react';
import VerifiedCheck from '../VerifiedCheck';
import { timeAgo, eventHref } from './ThePulseStrip';
import { linkHoverProps } from '../../lib/nativeCache';
import PulseReactions from './PulseReactions';

const ICON_MAP = {
  'shield-check': ShieldCheck, 'badge-check': BadgeCheck, 'sparkles': Sparkles,
  'newspaper': Newspaper, 'film': Film, 'video': Video, 'pencil': Pencil,
  'link': LinkIcon, 'flag': Flag, 'arrow-right-left': ArrowRightLeft,
  'trophy': Trophy, 'award': Award, 'handshake': Handshake, 'radio': Radio,
  'users-plus': UserPlus, 'user-plus': UserPlus, 'list': ListChecks,
  'rocket': Rocket, 'megaphone': Megaphone, 'flame': Flame,
  'activity': Activity, 'users': Users,
};

const TONE = {
  blue: 'text-blue-300', emerald: 'text-emerald-300', green: 'text-green-300',
  amber: 'text-amber-300', slate: 'text-white/70', purple: 'text-purple-300',
  orange: 'text-orange-300', cyan: 'text-cyan-300', rose: 'text-rose-300',
};

/** Convert a raw url to an embeddable player URL for YouTube/Vimeo.
 *  Returns { kind: 'iframe'|'video'|'none', src } */
export const parseEmbed = (url) => {
  if (!url || typeof url !== 'string') return { kind: 'none', src: '' };
  const u = url.trim();
  // YouTube (watch, youtu.be, shorts, embed)
  const ytMatch = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{6,})/);
  if (ytMatch) {
    return { kind: 'iframe', src: `https://www.youtube-nocookie.com/embed/${ytMatch[1]}?rel=0&modestbranding=1&playsinline=1&iv_load_policy=3` };
  }
  // Vimeo — preserve unlisted hash so private videos actually play.
  const vmMatch = u.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([A-Za-z0-9]+))?/);
  if (vmMatch) {
    const base = `https://player.vimeo.com/video/${vmMatch[1]}`;
    return {
      kind: 'iframe',
      src: vmMatch[2] ? `${base}?h=${vmMatch[2]}` : base,
    };
  }
  // Direct mp4/mov/webm
  if (/\.(mp4|mov|webm|m4v)($|\?)/i.test(u)) {
    return { kind: 'video', src: u };
  }
  return { kind: 'none', src: u };
};

/** iter290 · Resolve a founder-attached link. Scheme-less URLs get
 *  https:// prefixed; flythouse.com / same-host URLs collapse to an
 *  internal path so <Link> client-side navigation handles them instead
 *  of the browser treating them as relative hrefs (which used to land
 *  on the /:slug athlete catch-all → "not on the network yet"). */
export const resolveLink = (url) => {
  const raw = String(url || '').trim();
  if (!raw) return { internal: false, href: '#' };
  if (raw.startsWith('/')) return { internal: true, to: raw };
  const abs = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const u = new URL(abs);
    const host = u.hostname.replace(/^www\./, '').toLowerCase();
    const here = window.location.hostname.replace(/^www\./, '').toLowerCase();
    if (host === 'flythouse.com' || host === here) {
      return { internal: true, to: `${u.pathname}${u.search}${u.hash}` || '/' };
    }
    return { internal: false, href: abs };
  } catch {
    return { internal: false, href: abs };
  }
};

const PulseEventDetailModal = ({ ev, onClose, onEventUpdate }) => {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onEsc);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onEsc);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  if (!ev) return null;

  const Icon = ICON_MAP[ev.icon] || Activity;
  const toneClass = TONE[ev.tone] || TONE.slate;
  const md = ev.metadata || {};
  const videoUrl = md.video_url || '';
  const links = Array.isArray(md.links) ? md.links : [];
  const linkedAthlete = md.linked_athlete && md.linked_athlete.slug ? md.linked_athlete : null;
  const embed = parseEmbed(videoUrl);
  const profileHref = eventHref(ev);
  const hasProfileTarget = profileHref && profileHref !== '/pulse';

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="pulse-detail-modal"
    >
      {/* Backdrop */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close details"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        data-testid="pulse-detail-backdrop"
      />
      {/* Sheet */}
      <div
        className="relative w-full sm:max-w-lg bg-neutral-950 ring-1 ring-white/15 shadow-2xl sm:rounded-3xl rounded-t-3xl overflow-hidden max-h-[92vh] flex flex-col"
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
      >
        {/* Grabber (mobile only) */}
        <div className="sm:hidden pt-2 pb-1 flex justify-center">
          <span className="w-10 h-1 rounded-full bg-white/25" />
        </div>

        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/85 ring-1 ring-white/15 inline-flex items-center justify-center transition"
          data-testid="pulse-detail-close"
        >
          <X className="w-4 h-4 text-white" />
        </button>

        {/* Hero — video preferred, else image, else icon block */}
        <div className="w-full bg-black relative">
          {embed.kind === 'iframe' && (
            <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
              <iframe
                src={embed.src}
                title={ev.headline}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                data-testid="pulse-detail-iframe"
              />
            </div>
          )}
          {embed.kind === 'video' && (
            <video
              src={embed.src}
              controls
              playsInline
              className="w-full max-h-[60vh] bg-black"
              data-testid="pulse-detail-video"
            />
          )}
          {embed.kind === 'none' && ev.image_url && (
            <img
              src={ev.image_url}
              alt=""
              className="w-full max-h-[50vh] object-cover"
              data-testid="pulse-detail-image"
            />
          )}

          {/* iter236 · Linked-athlete corner pill — clickable avatar
              overlaid in the bottom-right of the hero. Tapping jumps
              to /network/{slug}. Only rendered when a hero (video or
              image) is present so it has something to sit on. */}
          {linkedAthlete && (embed.kind !== 'none' || ev.image_url) && (
            <Link
              to={`/network/${linkedAthlete.slug}`}
              onClick={onClose}
              className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-black/75 hover:bg-black/90 ring-1 ring-white/25 backdrop-blur-sm transition group"
              data-testid="pulse-detail-linked-athlete"
              aria-label={`Open ${linkedAthlete.name}'s profile`}
              {...linkHoverProps(linkedAthlete.slug)}
            >
              {linkedAthlete.photo ? (
                <img
                  src={linkedAthlete.photo}
                  alt=""
                  className="w-7 h-7 rounded-full object-cover ring-1 ring-white/30"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-blue-500/40 ring-1 ring-white/30 inline-flex items-center justify-center">
                  <UserIcon className="w-3.5 h-3.5 text-white" />
                </div>
              )}
              <span className="text-[11px] font-bold text-white uppercase tracking-wider max-w-[130px] truncate">
                {linkedAthlete.name}
              </span>
              <ArrowRight className="w-3 h-3 text-white/70 group-hover:text-white transition" />
            </Link>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-4">
          {/* Meta row */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ring-1 ring-white/15 bg-white/[0.05] ${toneClass}`}>
              <Icon className="w-3 h-3" />
              {(ev.event_type || '').replace(/_/g, ' ')}
            </span>
            <span className="text-[11px] text-white/40 font-mono">· {timeAgo(ev.created_at)}</span>
          </div>

          {/* Actor */}
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-white">{ev.actor_name}</p>
            {ev.verified && <VerifiedCheck size={14} />}
          </div>

          {/* Headline */}
          <h2 className="text-xl sm:text-2xl font-bold text-white leading-tight" data-testid="pulse-detail-headline">
            {ev.headline}
          </h2>

          {/* Description */}
          {ev.description && (
            <p className="text-sm text-white/75 leading-relaxed whitespace-pre-wrap" data-testid="pulse-detail-description">
              {ev.description}
            </p>
          )}

          {/* Custom links as chips */}
          {links.length > 0 && (
            <div className="pt-1" data-testid="pulse-detail-links">
              <p className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">
                Links
              </p>
              <div className="flex flex-wrap gap-2">
                {links.map((lk, i) => {
                  const r = resolveLink(lk.url);
                  const ChipIcon = lk.story ? Newspaper : r.internal ? ArrowRight : ExternalLink;
                  const cls = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.14] ring-1 ring-white/15 text-white text-xs font-semibold transition';
                  return r.internal ? (
                    <Link
                      key={`${lk.url}-${i}`}
                      to={r.to}
                      onClick={onClose}
                      className={cls}
                      data-testid={`pulse-detail-link-${i}`}
                    >
                      <ChipIcon className="w-3 h-3 text-white/60" />
                      <span className="truncate max-w-[220px]">{lk.label}</span>
                    </Link>
                  ) : (
                    <a
                      key={`${lk.url}-${i}`}
                      href={r.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cls}
                      data-testid={`pulse-detail-link-${i}`}
                    >
                      <ChipIcon className="w-3 h-3 text-white/60" />
                      <span className="truncate max-w-[220px]">{lk.label}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {/* Optional metadata pills (sport / team / category) */}
          {(md.sport || md.team || md.category) && (
            <p className="text-[11px] uppercase tracking-widest text-white/45">
              {[md.sport, md.team, md.category].filter(Boolean).join(' · ')}
            </p>
          )}

          {/* iter234 · Reactions — Heart + Clap for anonymous appreciation */}
          <div className="pt-2 border-t border-white/[0.06] flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold mr-1">
              Show some love
            </span>
            <PulseReactions ev={ev} size="md" onUpdate={onEventUpdate} />
          </div>
        </div>

        {/* Footer CTA */}
        {hasProfileTarget && (
          <div className="px-5 sm:px-6 py-4 border-t border-white/10 bg-black/40">
            <Link
              to={profileHref}
              onClick={onClose}
              className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-white text-black text-xs font-black uppercase tracking-widest hover:bg-blue-200 transition"
              data-testid="pulse-detail-cta"
            >
              {ev.cta_label || 'Open'} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default PulseEventDetailModal;
